import { useEffect, useRef, useState } from 'react';

function useCountUp(target: number, duration: number, start: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

const stats = [
  { value: 20, suffix: '億+', label: 'EC売上', sub: '複数ブランドにわたる実証済みの戦略' },
  { value: 900, suffix: '冠+', label: 'ランキング1位', sub: '各カテゴリーでの圧倒的な市場支配' },
  { value: 100, suffix: '+', label: '商品開発実績', sub: '0から1を生み出す、確かなプロセス' },
  { value: 21, suffix: '年', label: '美容師キャリア', sub: '現場から生まれた、本物の顧客理解' },
];

const awards = [
  'Amazon ベストセラー 1位',
  '楽天市場 年間ランキング 1位',
  'Yahoo!ショッピング 月間MVP',
  '各種業界アワード 受賞多数',
  'メディア掲載 100件以上',
];

export default function EvidenceSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const c0 = useCountUp(stats[0].value, 2000, visible);
  const c1 = useCountUp(stats[1].value, 2200, visible);
  const c2 = useCountUp(stats[2].value, 1800, visible);
  const c3 = useCountUp(stats[3].value, 1600, visible);
  const counts = [c0, c1, c2, c3];

  return (
    <section id="evidence" className="relative bg-white py-24 md:py-36 overflow-hidden" ref={sectionRef}>
      {/* Subtle background texture */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/64371fa074ee4ed41d415d1c46c204f1.jpg"
          alt=""
          className="w-full h-full object-cover opacity-[0.06]"
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          className={`mb-16 md:mb-20 transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-8 h-px bg-[#b4a078]" />
            <span className="text-[#b4a078] text-[10px] tracking-[0.4em] uppercase">The Evidence</span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2
              className="text-[#1a1a1a] font-light leading-tight"
              style={{ fontFamily: "'Noto Serif JP', serif", fontSize: 'clamp(1.8rem, 4vw, 3.5rem)' }}
            >
              数字が証明する、<br />
              <span className="text-[#1a1a1a]/35">圧倒的な実績。</span>
            </h2>
            <p className="text-[#1a1a1a]/40 text-xs leading-relaxed max-w-sm">
              今、この瞬間も最前線で動いているプロジェクトから得られる、
              超秘匿性の高い成功実例を即座に還元します。
            </p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#1a1a1a]/6 rounded-2xl overflow-hidden mb-16">
          {stats.map((stat, i) => (
            <div
              key={stat.label}
              className={`bg-[#fafaf8] p-8 md:p-10 transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              <div
                className="font-light text-[#1a1a1a] mb-2"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2.5rem, 5vw, 4rem)' }}
              >
                <span>{counts[i]}</span>
                <span className="text-[#b4a078]">{stat.suffix}</span>
              </div>
              <p className="text-[#1a1a1a]/55 text-xs tracking-widest mb-2">{stat.label}</p>
              <p className="text-[#1a1a1a]/30 text-[10px] leading-relaxed">{stat.sub}</p>
            </div>
          ))}
        </div>

        {/* Awards & Evidence */}
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-8 transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ transitionDelay: '600ms' }}
        >
          {/* Awards list */}
          <div className="border border-[#1a1a1a]/8 rounded-2xl p-8 bg-[#fafaf8]">
            <p className="text-[#b4a078] text-[10px] tracking-[0.3em] uppercase mb-6">Awards &amp; Recognition</p>
            <div className="space-y-4">
              {awards.map((award, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#b4a078] flex-shrink-0" />
                  <span className="text-[#1a1a1a]/55 text-sm">{award}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Live projects */}
          <div className="border border-[#1a1a1a]/8 rounded-2xl p-8 relative overflow-hidden bg-[#fafaf8]">
            <div className="absolute top-6 right-6 flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-emerald-600/70 text-[9px] tracking-widest uppercase">Live Now</span>
            </div>
            <p className="text-[#b4a078] text-[10px] tracking-[0.3em] uppercase mb-6">現在進行中のプロジェクト</p>
            <p
              className="text-[#1a1a1a]/75 text-lg font-light leading-relaxed mb-6"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              今この瞬間も、最前線で<br />プロジェクトが動いています。
            </p>
            <p className="text-[#1a1a1a]/40 text-xs leading-relaxed">
              現場で得た最新の知見・成功パターン・失敗の教訓を、
              リアルタイムであなたの事業に還元します。
              教科書ではなく、生きた戦略を。
            </p>
            <div className="mt-8 pt-6 border-t border-[#1a1a1a]/8">
              <div className="flex items-center justify-between">
                <span className="text-[#1a1a1a]/30 text-xs">稼働中プロジェクト</span>
                <span className="text-[#b4a078] text-2xl font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  複数同時進行
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
